import React, { useCallback, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Routes as AppRoutes } from "./routes";
import "./App.css";
import "./features/dashboard/dashboard.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Login from "./features/auth/login/Login";
import Dashboard from "./features/dashboard/Dashboard";
import Managers from "./features/admin/manager/Managers";
import Employee from "./features/admin/employee/Employee";
import Supplier from "./features/admin/supplier/Supplier";
import CreatePassword from "./features/auth/createPassword/CreatePassword";
import ResetPassword from "./features/auth/resetPassword/ResetPassword";
import WashingArea from "./features/Receiving/washing/WashingArea";
import { isTokenExpired } from "./helpers/AccessControlUtils";
import { useDispatch, useSelector } from "react-redux";
import { setRedirectPath, setSystemVariables, setUserTimezone } from "./app/globalSlice";
import { Navigate, Outlet } from "react-router";
import Header from "./components/layout/header/Header";
import Sidebar from "./components/layout/sidebar/Sidebar";
import { Toaster } from "react-hot-toast";
import ForgetPassword from "./features/auth/forgetPassword/ForgetPassword";
import Unloading from "./features/Receiving/unloading/Unloading";
import RejectionLog from "./features/Receiving/rejectionLog/RejectionLog";
import ReceivingDashboard from "./features/Receiving/receivingDashboard/ReceivingDashboard";
import ProcessingDashboard from "./features/processing/dashboard/ProcessingDashboard";
import RipeningDashboard from "./features/ripening/dashboard/RipeningDashboard";
import ColdStorage from "./features/ripening/coldStorage/ColdStorage";
import RipeningRoom from "./features/ripening/ripeningRooms/RipeningRoom";
import Sorting from "./features/ripening/sorting/Sorting";
import Washing from "./features/ripening/washing/Washing";
import Processing from "./features/processing/processingOverview/Processing";
import Cooking from "./features/processing/cooking/Cooking";
import GasConsumptionPage from "./features/processing/cooking/gasconsumption/GasConsumptionPage";
import PackingDashboard from "./features/packing/overview/PackingDashboard";
import ProcessingWashing from "./features/processing/washing/ProcessingWashing";
import Packing from "./features/packing/packing/Packing";
import Bagging from "./features/packing/bagging/Bagging";
import PackingSorting from "./features/packing/sorting/PackingSorting";
import ReworkDashboard from "./features/packing/reworkoverview/ReworkDashboard";
import Rework from "./features/packing/rework/Rework";
import Humid from "./features/packing/humid/Humid";
import GreenRoom from "./features/packing/greenroom/GreenRoom";
import Refrigeration from "./features/packing/refrigeration/Refrigeration";
import ReworkRefrigeration from "./features/packing/reworkrefrigeration/ReworkRefrigeration";
import PackingWashing from "./features/packing/washing/PackingWashing";

import BrokerageDashboard from "./features/brokerage/overview/BrokerageDashboard";
import MaterialEntered from "./features/brokerage/materialEntered/MaterialEntered";
import PackingAndSorting from "./features/brokerage/packingAndSorting/PackingAndSorting";
import Turn2 from "./features/brokerage/turn2/Turn2";
import Mixing from "./features/pasting/Mixing";
import Pasting from "./features/pasting/Pasting";
import BrokerageSorting from "./features/brokerage/sorting/BrokerageSorting";
import BrokeragePacking from "./features/brokerage/packing/BrokeragePacking";
import BrokerageRefrigeration from "./features/brokerage/refrigeration/BrokerageRefrigeration";
import PastingDashboard from "./features/pasting/dashboard/PastingDashboard";
import PastingSorting from "./features/pasting/sorting/PastingSorting";
import MixingCart from "./features/settings/mixingCart/MixingCart";
import Cart from "./features/pasting/cart/Cart";
import SystemVariables from "./features/admin/systemVariables/SystemVariables";
import AttendenceReport from "./features/attendence/attendenceReport/AttendenceReport";
import StaffAttendence from "./features/attendence/staffAttendence/StaffAttendence";
import EmployeeAttendence from "./features/employeelogs/EmployeeAttendence";
import EmployeeAttendenceReport from "./features/employeelogs/attendanceReport/EmployeeAttendenceReport";
import EmployeeDetails from "./features/employeelogs/employeeDetail/EmployeeDetails";
import Billcreation from "./features/expense/Billcreation";
import BillListing from "./features/expense/BillListing";
import BillingReport from "./features/expense/BillingReport";
import ExpenseDashboard from "./features/expense/overview/ExpenseDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import RolePermission from "./features/admin/rolePermission/RolePermission";
import ReworkPacking from "./features/packing/reworkpacking/ReworkPacking";
import Toasted from "./features/packing/toasted/Toasted";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import { usePageTitle } from "./hooks/usePageTitle";
import { useGetEnvVariablesQuery } from "./features/admin/systemVariables/systemVariablesApi";

// Component to update page title on route changes
const PageTitleUpdater = ({ children }) => {
    usePageTitle();
    return children;
};

function App() {
    const error = useSelector((state) => state.global.error);
    const dispatch = useDispatch();

    const { data: envVariables } = useGetEnvVariablesQuery(
        { per_page: "all" },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    useEffect(() => {
        if (envVariables?.data) {

            const settings = envVariables.data.reduce((acc, item) => {
                acc[item.key] = item.value;
                return acc;
            }, {});

            dispatch(setSystemVariables(settings));
        }
    }, [envVariables, dispatch]);


    // Initialize timezone from localStorage on app mount
    useEffect(() => {
        const savedTimezone = localStorage.getItem("userTimezone");
        if (savedTimezone) {
            dispatch(setUserTimezone(savedTimezone));
        }
    }, [dispatch]);

    const PublicRoutes = React.memo(() => {
        return (
            <>
                <Outlet />
            </>
        );
    });
    const PrivateRoutes = () => {
        const isUserLoggedIn = localStorage.getItem("authToken");
        const isAuthenticated = isUserLoggedIn && !isTokenExpired();

        if (!isAuthenticated) {
            dispatch(setRedirectPath(window.location.pathname + window.location.search + window.location.hash));
            return <Navigate to="/login" />;
        }

        return (
            <div className="wrapper">
                <div className="inner_wrapper">
                    <Header />
                    <Sidebar />
                    <Outlet />
                </div>
            </div>
        );
    };


    return (
        <Router>
            <PageTitleUpdater>
                <ErrorBoundary>
                <Toaster position="top-center" reverseOrder={false} toastOptions={{ duration: 2000 }} />
                {!error &&
                    <Routes>
                    {/* Public Routes without Layout */}
                    <Route element={<PublicRoutes />}>
                        {!(localStorage.getItem("authToken") && !isTokenExpired()) && <Route path={AppRoutes.Default.path} element={<Login />} />}
                        <Route path={AppRoutes.Login.path} element={<Login />} />
                        <Route path={AppRoutes.CreatePassword.path} element={<CreatePassword />} />
                        <Route path={AppRoutes.ResetPassword.path} element={<ResetPassword />} />
                        <Route path={AppRoutes.ForgotPassword.path} element={<ForgetPassword />} />
                    </Route>
                    {/* Private Routes with Layout */}
                    <Route element={<PrivateRoutes />}>
                        {(localStorage.getItem("authToken") && !isTokenExpired()) && <Route path={AppRoutes.Default.path} element={<Dashboard />} />}
                        <Route path={AppRoutes.Dashboard.path} element={<Dashboard />} />
                        <Route path={AppRoutes.Supplier.path} element={<ProtectedRoute path={AppRoutes.Supplier.path}><Supplier /></ProtectedRoute>} />
                        <Route path={AppRoutes.Manager.path} element={<ProtectedRoute path={AppRoutes.Manager.path}><Managers /></ProtectedRoute>} />
                        <Route path={AppRoutes.Employee.path} element={<ProtectedRoute path={AppRoutes.Employee.path}><Employee /></ProtectedRoute>} />
                        <Route path={AppRoutes.Unloading.path} element={<ProtectedRoute path={AppRoutes.Unloading.path}><Unloading /></ProtectedRoute>} />
                        <Route path={AppRoutes.WashingArea.path} element={<ProtectedRoute path={AppRoutes.WashingArea.path}><WashingArea /></ProtectedRoute>} />
                        <Route path={AppRoutes.ReceivingDashboard.path} element={<ProtectedRoute path={AppRoutes.ReceivingDashboard.path}><ReceivingDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.RejectionLog.path} element={<ProtectedRoute path={AppRoutes.RejectionLog.path}><RejectionLog /></ProtectedRoute>} />
                        <Route path={AppRoutes.RipeningDashboard.path} element={<ProtectedRoute path={AppRoutes.RipeningDashboard.path}><RipeningDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.ColdStorage.path} element={<ProtectedRoute path={AppRoutes.ColdStorage.path}><ColdStorage /></ProtectedRoute>} />
                        <Route path={AppRoutes.RipeningRoom.path} element={<ProtectedRoute path={AppRoutes.RipeningRoom.path}><RipeningRoom /></ProtectedRoute>} />
                        <Route path={AppRoutes.Sorting.path} element={<ProtectedRoute path={AppRoutes.Sorting.path}><Sorting /></ProtectedRoute>} />
                        <Route path={AppRoutes.RipeningWashing.path} element={<ProtectedRoute path={AppRoutes.RipeningWashing.path}><Washing /></ProtectedRoute>} />
                        <Route path={AppRoutes.ProcessingDashboard.path} element={<ProtectedRoute path={AppRoutes.ProcessingDashboard.path}><ProcessingDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.Processing.path} element={<ProtectedRoute path={AppRoutes.Processing.path}><Processing /></ProtectedRoute>} />
                        <Route path={AppRoutes.Cooking.path} element={<ProtectedRoute path={AppRoutes.Cooking.path}><Cooking /></ProtectedRoute>} />
                        <Route path={AppRoutes.GasConsumption.path} element={<ProtectedRoute path={AppRoutes.GasConsumption.path}><GasConsumptionPage /></ProtectedRoute>} />
                        <Route path={AppRoutes.PackingDashboard.path} element={<ProtectedRoute path={AppRoutes.PackingDashboard.path}><PackingDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.Packing.path} element={<ProtectedRoute path={AppRoutes.Packing.path}><Packing /></ProtectedRoute>} />
                        <Route path={AppRoutes.Bagging.path} element={<ProtectedRoute path={AppRoutes.Bagging.path}><Bagging /></ProtectedRoute>} />
                        <Route path={AppRoutes.PackingSorting.path} element={<ProtectedRoute path={AppRoutes.PackingSorting.path}><PackingSorting /></ProtectedRoute>} />
                        <Route path={AppRoutes.ProcessingWashing.path} element={<ProtectedRoute path={AppRoutes.ProcessingWashing.path}><ProcessingWashing /></ProtectedRoute>} />
                        <Route path={AppRoutes.ReworkDashboard.path} element={<ProtectedRoute path={AppRoutes.ReworkDashboard.path}><ReworkDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.Rework.path} element={<ProtectedRoute path={AppRoutes.Rework.path}><Rework /></ProtectedRoute>} />
                        <Route path={AppRoutes.Humid.path} element={<ProtectedRoute path={AppRoutes.Humid.path}><Humid /></ProtectedRoute>} />
                        <Route path={AppRoutes.GreenRoom.path} element={<ProtectedRoute path={AppRoutes.GreenRoom.path}><GreenRoom /></ProtectedRoute>} />
                        <Route path={AppRoutes.Refrigeration.path} element={<ProtectedRoute path={AppRoutes.Refrigeration.path}><Refrigeration /></ProtectedRoute>} />
                        <Route path={AppRoutes.ReworkRefrigeration.path} element={<ProtectedRoute path={AppRoutes.ReworkRefrigeration.path}><ReworkRefrigeration /></ProtectedRoute>} />
                        <Route path={AppRoutes.PackingWashing.path} element={<ProtectedRoute path={AppRoutes.PackingWashing.path}><PackingWashing /></ProtectedRoute>} />
                        <Route path={AppRoutes.BrokerageDashboard.path} element={<ProtectedRoute path={AppRoutes.BrokerageDashboard.path}><BrokerageDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.MaterialEntered.path} element={<ProtectedRoute path={AppRoutes.MaterialEntered.path}><MaterialEntered /></ProtectedRoute>} />
                        <Route path={AppRoutes.PackingAndSorting.path} element={<ProtectedRoute path={AppRoutes.PackingAndSorting.path}><PackingAndSorting /></ProtectedRoute>} />
                        <Route path={AppRoutes.BrokerageSorting.path} element={<ProtectedRoute path={AppRoutes.BrokerageSorting.path}><BrokerageSorting /></ProtectedRoute>} />
                        <Route path={AppRoutes.BrokeragePacking.path} element={<ProtectedRoute path={AppRoutes.BrokeragePacking.path}><BrokeragePacking /></ProtectedRoute>} />
                        <Route path={AppRoutes.BrokerageRefrigeration.path} element={<ProtectedRoute path={AppRoutes.BrokerageRefrigeration.path}><BrokerageRefrigeration /></ProtectedRoute>} />
                        <Route path={AppRoutes.RefrigerationArea.path} element={<ProtectedRoute path={AppRoutes.RefrigerationArea.path}><BrokerageRefrigeration /></ProtectedRoute>} />
                        <Route path={AppRoutes.AttendenceReport.path} element={<ProtectedRoute path={AppRoutes.AttendenceReport.path}><AttendenceReport /></ProtectedRoute>} />
                        <Route path={AppRoutes.StaffAttendence.path} element={<ProtectedRoute path={AppRoutes.StaffAttendence.path}><StaffAttendence /></ProtectedRoute>} />
                        <Route path={AppRoutes.EmployeeAttendence.path} element={<ProtectedRoute path={AppRoutes.EmployeeAttendence.path}><EmployeeAttendence /></ProtectedRoute>} />
                        <Route path={AppRoutes.EmployeeAttendenceReport.path} element={<ProtectedRoute path={AppRoutes.EmployeeAttendenceReport.path}><EmployeeAttendenceReport /></ProtectedRoute>} />
                        <Route path={AppRoutes.EmployeeDetails.path} element={<ProtectedRoute path={AppRoutes.EmployeeDetails.path}><EmployeeDetails /></ProtectedRoute>} />
                        <Route path={AppRoutes.ExpenseDashboard.path} element={<ProtectedRoute path={AppRoutes.ExpenseDashboard.path}><ExpenseDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.Billcreation.path} element={<ProtectedRoute path={AppRoutes.Billcreation.path}><Billcreation /></ProtectedRoute>} />
                        <Route path={AppRoutes.BillListing.path} element={<ProtectedRoute path={AppRoutes.BillListing.path}><BillListing /></ProtectedRoute>} />
                        <Route path={AppRoutes.BillingReport.path} element={<ProtectedRoute path={AppRoutes.BillingReport.path}><BillingReport /></ProtectedRoute>} />
                        <Route path={AppRoutes.BillReportid.path} element={<ProtectedRoute path={AppRoutes.BillReportid.path}><Billcreation /></ProtectedRoute>} />
                        <Route path={AppRoutes.RolePermission.path} element={<ProtectedRoute path={AppRoutes.RolePermission.path}><RolePermission /></ProtectedRoute>} />
                        <Route path={AppRoutes.ReworkPacking.path} element={<ProtectedRoute path={AppRoutes.ReworkPacking.path}><ReworkPacking /></ProtectedRoute>} />
                        <Route path={AppRoutes.Toasted.path} element={<ProtectedRoute path={AppRoutes.Toasted.path}><Toasted /></ProtectedRoute>} />
                        <Route path={AppRoutes.BrokerageTurn2.path} element={<ProtectedRoute path={AppRoutes.BrokerageTurn2.path}><Turn2 /></ProtectedRoute>} />
                        <Route path={AppRoutes.Mixing.path} element={<ProtectedRoute path={AppRoutes.Mixing.path}><Mixing /></ProtectedRoute>} />
                        <Route path={AppRoutes.PastingPacking.path} element={<ProtectedRoute path={AppRoutes.PastingPacking.path}><Pasting /></ProtectedRoute>} />
                        <Route path={AppRoutes.PastingDashboard.path} element={<ProtectedRoute path={AppRoutes.PastingDashboard.path}><PastingDashboard /></ProtectedRoute>} />
                        <Route path={AppRoutes.PastingSorting.path} element={<ProtectedRoute path={AppRoutes.PastingSorting.path}><PastingSorting /></ProtectedRoute>} />
                        <Route path={AppRoutes.MixingCart.path} element={<ProtectedRoute path={AppRoutes.MixingCart.path}><MixingCart /></ProtectedRoute>} />
                        <Route path={AppRoutes.PastingCart.path} element={<ProtectedRoute path={AppRoutes.PastingCart.path}><Cart /></ProtectedRoute>} />
                        <Route path={AppRoutes.SystemVariables.path} element={<ProtectedRoute path={AppRoutes.SystemVariables.path}><SystemVariables /></ProtectedRoute>} />
                    </Route>
                </Routes>
            }

        </ErrorBoundary>
            </PageTitleUpdater>
        </Router>
    );
}

export default App;    
